import { motion } from "framer-motion";
import { Layers, LayoutList, MonitorSmartphone, MessageSquare, SearchCheck, Puzzle } from "lucide-react";
import { SERVICES, STEPS, FAQ, QUOTE, MARQUEE } from "@/content/site";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useUI } from "@/context/UIContext";
import { Reveal, SectionLabel } from "./Reveal";

const ICONS = [Layers, LayoutList, MonitorSmartphone, MessageSquare, SearchCheck, Puzzle];
const SPANS = ["sm:col-span-2 lg:row-span-2", "", "", "", "", "sm:col-span-2 lg:col-span-4"];

export function Marquee() {
  const row = [...MARQUEE, ...MARQUEE];
  return (
    <div className="overflow-hidden border-y border-line py-5" aria-hidden="true">
      <div className="marquee">
        {row.map((m, i) => (
          <span key={i} className="flex items-center gap-8 pr-8 font-display text-[28px] font-medium tracking-tight text-ink/80 sm:text-[40px]">
            {i % 3 === 1 ? <em className="font-serif font-normal">{m}</em> : m}<span className="text-gold">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function Services() {
  return (
    <section className="py-20 sm:py-28" data-testid="services-section" aria-labelledby="leistungen-h">
      <div className="wrap">
        <Reveal>
          <SectionLabel n="03">Leistungen & Nutzen</SectionLabel>
          <h2 id="leistungen-h" className="mt-5 max-w-[760px] font-display text-[34px] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-5xl">
            Alles, was ein überzeugender Auftritt <span className="font-serif font-normal italic">braucht.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[i];
            const big = i === 0;
            return (
              <Reveal key={s.n} delay={i * 0.05} className={SPANS[i]}>
                <motion.article whileHover={{ y: -4 }} transition={{ duration: 0.25 }} data-testid={`service-card-${s.n}`}
                  className={`group flex h-full flex-col justify-between rounded-2xl border p-6 sm:p-7 ${big ? "border-ink bg-ink text-paper" : "border-line bg-white"}`}>
                  <div className="flex items-start justify-between">
                    <span className={`grid h-11 w-11 place-items-center rounded-full ${big ? "bg-gold text-ink" : "bg-paper text-ink"}`}><Icon className="h-5 w-5" /></span>
                    <span className={`font-mono text-xs ${big ? "text-zinc-400" : "text-mute"}`}>{s.n}</span>
                  </div>
                  <div className={big ? "mt-16" : "mt-8"}>
                    <h3 className={`font-display font-semibold tracking-tight ${big ? "text-[32px] leading-tight sm:text-[40px]" : "text-[20px]"}`}>{s.title}</h3>
                    <p className={`mt-2 leading-relaxed ${big ? "max-w-[420px] text-[16px] text-zinc-300" : "text-[14px] text-mute"}`}>{s.text}</p>
                  </div>
                </motion.article>
              </Reveal>
            );
          })}
        </div>
        <p className="mt-5 text-[13px] text-mute">Zusatzfunktionen werden individuell mit Ihnen abgestimmt.</p>
      </div>
    </section>
  );
}

export function Process() {
  const { openDemo } = useUI();
  return (
    <section id="ablauf" className="border-t border-line bg-white py-20 sm:py-28" data-testid="process-section">
      <div className="wrap grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
        <Reveal className="lg:sticky lg:top-28 lg:self-start">
          <SectionLabel n="04">Ablauf</SectionLabel>
          <h2 className="mt-5 font-display text-[34px] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-5xl">In drei Schritten zu Ihrem <span className="font-serif font-normal italic">ersten Eindruck.</span></h2>
          <p className="mt-5 max-w-[440px] text-base text-mute sm:text-lg">Die kostenlose Demo zeigt eine Richtung – Gestaltung und Umfang Ihrer Website entwickeln wir danach gemeinsam.</p>
          <button type="button" data-testid="process-demo-cta" onClick={() => openDemo()} className="btn btn-ink mt-8">Kostenlose Demo anfordern</button>
        </Reveal>
        <ol className="relative">
          <span className="absolute bottom-6 left-[27px] top-6 w-px bg-line" aria-hidden="true" />
          {STEPS.map((s, i) => (
            <Reveal as="li" key={s.n} delay={i * 0.08} className="relative flex gap-6 pb-12 last:pb-0">
              <span className="relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-full border border-line bg-paper font-mono text-[14px] font-semibold">{s.n}</span>
              <div className="pt-2.5">
                <h3 className="font-display text-[24px] font-semibold tracking-tight sm:text-[28px]">{s.title}</h3>
                <p className="mt-2 max-w-[520px] text-[15px] leading-relaxed text-mute sm:text-base">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function Founder() {
  return (
    <section id="ueber-uns" className="py-20 sm:py-28" data-testid="founder-section">
      <div className="wrap">
        <Reveal><SectionLabel n="05">Über uns</SectionLabel></Reveal>
        <div className="mt-10 grid items-center gap-12 lg:grid-cols-[auto_1fr] lg:gap-20">
          <Reveal>
            <div className="relative mx-auto grid aspect-[4/5] w-[240px] place-items-center rounded-[4px] border border-line bg-white shadow-[0_30px_60px_-40px_rgba(26,28,30,.45)] sm:w-[300px]" data-testid="founder-monogram">
              <div className="absolute inset-3 border border-gold/50" />
              <span className="font-serif text-[120px] italic leading-none text-ink sm:text-[150px]">P<span className="text-gold-deep">N</span></span>
              <span className="eyebrow absolute bottom-6 text-[11px] text-mute">Wien · Österreich</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <blockquote>
              <p className="font-serif text-[30px] leading-[1.15] text-ink sm:text-[44px] lg:text-[52px]" data-testid="founder-quote">„{QUOTE}“</p>
              <footer className="mt-8 flex items-center gap-4">
                <span className="h-px w-12 bg-gold" />
                <span><span className="block font-display text-[18px] font-semibold">Pascal Niedermeyer</span><span className="text-[14px] text-mute">Geschäftsführer</span></span>
              </footer>
            </blockquote>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function Faq() {
  return (
    <section className="border-t border-line bg-white py-20 sm:py-28" data-testid="faq-section" aria-labelledby="faq-h">
      <div className="wrap grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
        <Reveal>
          <SectionLabel n="06">Häufige Fragen</SectionLabel>
          <h2 id="faq-h" className="mt-5 font-display text-[34px] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-5xl">Gut zu <span className="font-serif font-normal italic">wissen.</span></h2>
        </Reveal>
        <Accordion type="single" collapsible className="border-t border-line">
          {FAQ.map((f, i) => (
            <AccordionItem key={f.q} value={`f${i}`} className="border-line">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left font-display text-[18px] font-semibold tracking-tight hover:no-underline sm:text-[21px]">{f.q}</AccordionTrigger>
              <AccordionContent className="pb-6 text-[15px] leading-relaxed text-mute sm:text-base">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
